import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useRef, useEffect} from 'react';
import AceEditor from 'react-ace';

import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import AsyncButton, {AsyncButtonLoadingBehavior} from 'ui-library/buttons/AsyncButton';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SettingsField from 'ui-library/SettingsField';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {WebhookPlaygroundPayloadResponse} from '../BenchlingAppDAO';
import {ResponseState} from './BenchlingAppWebhookPlayground';

enum WebhookPlaygroundPreviewTab {
  PAYLOAD = 'PAYLOAD',
  DETAILS = 'DETAILS',
}

export function disableEditorHighlightAndCursor(aceEditor: AceEditor): void {
  // To completely disable the editor, you must also set the AceEditor's readOnly prop to true and
  // highlightActiveLine prop to false.

  // @ts-expect-error The AceEditor type does not come with the "editor" property, but it is documented to exist
  // https://github.com/securingsincity/react-ace/blob/master/docs/FAQ.md
  const editor = aceEditor.editor;

  editor.renderer.$highlightGutterLine = false;
  editor.renderer.$cursorLayer.element.style.display = 'none';
}

interface WebhookPlaygroundPreviewProps {
  messageTypes: Array<OptionData>;
  selectedMessageType: string | null;
  setSelectedMessageType: (messageType: string | null) => void;
  webhookURL: string | null;
  payloadData: WebhookPlaygroundPayloadResponse;
  handleClickSendTest: () => Promise<void>;
  responseState: ResponseState;
}

export default function WebhookPlaygroundPreview(props: WebhookPlaygroundPreviewProps): JSX.Element {
  const [currentTab, setCurrentTab] = useState<WebhookPlaygroundPreviewTab>(
    WebhookPlaygroundPreviewTab.PAYLOAD
  );
  const editorRef = useRef<AceEditor | null>(null);

  useEffect(() => {
    const editorValue = editorRef.current;
    if (editorValue) {
      disableEditorHighlightAndCursor(editorValue);
    }
  }, [currentTab]); // AceEditor needs to be re-initialized when the tab changes

  return (
    <div>
      <div className={css(styles.marginBottom)}>
        Preview message payloads and send them to your app. Benchling sends a request with the message payload
        to your test webhook URL when you send a test.
      </div>

      <label>Message</label>
      <Row className={css(styles.marginBottom)}>
        <Col md={9}>
          <DropdownSelector
            allowNull={false}
            searchable={true}
            selectedValue={props.selectedMessageType}
            onChange={props.setSelectedMessageType}
            optionDatas={props.messageTypes}
          />
        </Col>
        <Col md={3}>
          <AsyncButton
            buttonStyle={ButtonStyle.PRIMARY}
            loadingBehavior={AsyncButtonLoadingBehavior.DISABLED}
            size={ButtonSize.SMALL}
            onClick={props.handleClickSendTest}
            text="Send test"
            disabled={props.webhookURL == null || props.responseState === ResponseState.LOADING}
          />
        </Col>
      </Row>

      {props.webhookURL == null && (
        <div className={css(styles.marginBottom)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="Testing isn't available."
            supportingInfo="Enter a valid webhook URL to test webhooks."
          />
        </div>
      )}

      <TabContainer
        activeTab={currentTab}
        className={classNames(css(styles.tabContainer), 'u-flex-grow')}
        onRequestTabChange={setCurrentTab as (value: WebhookPlaygroundPreviewTab) => void}
      >
        <Tab tabKey={WebhookPlaygroundPreviewTab.PAYLOAD} title="Payload">
          <AceEditor
            ref={editorRef}
            mode="json"
            theme="textmate"
            value={JSON.stringify(props.payloadData.payload, null, 4)}
            readOnly={true}
            width="100%"
            height="250px"
            wrapEnabled={true}
            highlightActiveLine={false}
            editorProps={{$blockScrolling: true}}
          />
        </Tab>

        <Tab tabKey={WebhookPlaygroundPreviewTab.DETAILS} title="Details">
          <div>
            <SettingsField
              label="URL"
              name="url"
              value={props.payloadData.webhookUrl ?? 'None'}
              isBeingCreated={false}
              onChange={noop}
              isWritable={false}
            />
            {props.payloadData.variableProps.length > 0 && (
              <div>
                <label>Payload Objects</label>
                <br />
                The example payload uses dummy data for the following properties:
                <ul>
                  {props.payloadData.variableProps.map((prop) => (
                    <li key={prop}>{prop}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Tab>
      </TabContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  marginBottom: {
    marginBottom: '12px',
  },
  tabContainer: {
    minHeight: '300px',
  },
});
